import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Alert, Select } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined, GlobalOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { userApi } from '../services/api';
import { generateDeviceFingerprint } from '../utils/deviceFingerprint';
import JSEncrypt from 'jsencrypt';

export default function LoginEnhanced() {
  const [loading, setLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const loginAttemptsRef = React.useRef(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTime, setLockTime] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(null);
  const captchaKeyRef = React.useRef(null);
  const [captchaImageUrl, setCaptchaImageUrl] = useState(null);
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const publicKeyRef = React.useRef(null);
  const navigate = useNavigate();
  const { t, language, changeLanguage } = useLanguage();

  useEffect(() => {
    const locked = localStorage.getItem('accountLocked');
    const lockTimestamp = localStorage.getItem('lockTimestamp');
    if (locked && lockTimestamp) {
      const now = Date.now();
      const lockDuration = 15 * 60 * 1000;
      const remainingTime = parseInt(lockTimestamp) + lockDuration - now;
      if (remainingTime > 0) {
        setIsLocked(true);
        setLockTime(Math.ceil(remainingTime / 1000 / 60));
        setTimeout(() => {
          localStorage.removeItem('accountLocked');
          localStorage.removeItem('lockTimestamp');
          localStorage.removeItem('loginAttempts');
          setIsLocked(false);
          setLoginAttempts(0);
          message.success(t('accountUnlocked'));
        }, remainingTime);
      } else {
        localStorage.removeItem('accountLocked');
        localStorage.removeItem('lockTimestamp');
        localStorage.removeItem('loginAttempts');
      }
    }
    const attempts = parseInt(localStorage.getItem('loginAttempts') || '0');
    loginAttemptsRef.current = attempts;
    setLoginAttempts(attempts);
    if (attempts >= 2) { setShowCaptcha(true); loadCaptcha(); }
    // 获取 RSA 公钥
    userApi.getPublicKey().then(({ data }) => {
      publicKeyRef.current = data?.data?.publicKey || null;
    }).catch(() => {});
  }, []);

  const loadCaptcha = async () => {
    try {
      const { data } = await userApi.getCaptcha();
      const key = data.data.captchaKey;
      setCaptchaKey(key);
      captchaKeyRef.current = key;
      setCaptchaImageUrl(userApi.getCaptchaImage(key) + '&t=' + Date.now());
    } catch (error) {
      console.error('Failed to load captcha:', error);
    }
  };

  const handleLogin = async (values) => {
    if (isLocked) { message.error(t('accountLockedDesc', lockTime)); return; }
    setLoading(true);
    try {
      // RSA 加密密码
      let password = values.password;
      if (publicKeyRef.current) {
        const encryptor = new JSEncrypt();
        encryptor.setPublicKey(publicKeyRef.current);
        const encrypted = encryptor.encrypt(values.password);
        if (encrypted) password = encrypted;
      }
      const { data } = await userApi.secureLogin({
        username: values.username,
        password,
        captcha: values.captcha,
        captchaKey: captchaKeyRef.current,
        deviceFingerprint: generateDeviceFingerprint()
      });
      if (data.code === '200') {
        const loginData = data.data;
        if (loginData.require2FA) {
          setTempToken(loginData.tempToken);
          setShow2FA(true);
          message.info(t('require2FA'));
          setLoading(false);
          return;
        }
        localStorage.removeItem('loginAttempts');
        localStorage.removeItem('accountLocked');
        localStorage.removeItem('lockTimestamp');
        localStorage.setItem('username', loginData.username);
        localStorage.setItem('isLogin', 'true');
        localStorage.setItem('userRole', loginData.role);
        localStorage.setItem('userType', loginData.userType);
        if (loginData.uid) localStorage.setItem('uid', loginData.uid);
        localStorage.setItem('user', JSON.stringify(loginData));
        // 获取用户渠道权限 & 补全 uid（等待完成后再跳转，确保 uid 可用）
        try {
          const r = await userApi.getMe();
          const me = r.data?.data;
          if (me) {
            if (me.uid) localStorage.setItem('uid', me.uid);
            localStorage.setItem('userChannel', me.channel || '');
            localStorage.setItem('userCurrencies', JSON.stringify(me.currencies || []));
          }
        } catch (_) {}
        message.success(t('loginSuccess'));
        navigate('/');
      } else {
        if (data.data?.requireCaptcha) { setShowCaptcha(true); loadCaptcha(); }
        handleLoginFailure(data.msg);
      }
    } catch (error) {
      handleLoginFailure(error.response?.data?.msg || t('loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLoginFailure = (errorMsg) => {
    const newAttempts = loginAttemptsRef.current + 1;
    loginAttemptsRef.current = newAttempts;
    setLoginAttempts(newAttempts);
    localStorage.setItem('loginAttempts', newAttempts.toString());
    if (newAttempts >= 5) {
      const lockTimestamp = Date.now();
      localStorage.setItem('accountLocked', 'true');
      localStorage.setItem('lockTimestamp', lockTimestamp.toString());
      setIsLocked(true);
      setLockTime(15);
      message.error(t('accountLockedDesc', 15));
    } else if (newAttempts >= 2) {
      setShowCaptcha(true);
      loadCaptcha();
      message.error(`${errorMsg}，${t('remainingAttempts', 5 - newAttempts)}`);
    } else {
      message.error(errorMsg);
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) { message.error(t('enter6Code')); return; }
    setLoading(true);
    try {
      const { data } = await userApi.verify2FALogin(tempToken, twoFACode);
      if (data.code === '200') {
        localStorage.removeItem('loginAttempts');
        localStorage.removeItem('accountLocked');
        localStorage.removeItem('lockTimestamp');
        const loginData = data.data;
        localStorage.setItem('username', loginData.username);
        localStorage.setItem('isLogin', 'true');
        localStorage.setItem('userRole', loginData.role);
        localStorage.setItem('userType', loginData.userType);
        if (loginData.uid) localStorage.setItem('uid', loginData.uid);
        localStorage.setItem('user', JSON.stringify(loginData));
        // 获取用户渠道权限 & 补全 uid（等待完成后再跳转）
        try {
          const r = await userApi.getMe();
          const me = r.data?.data;
          if (me) {
            if (me.uid) localStorage.setItem('uid', me.uid);
            localStorage.setItem('userChannel', me.channel || '');
            localStorage.setItem('userCurrencies', JSON.stringify(me.currencies || []));
          }
        } catch (_) {}
        message.success(t('loginSuccess'));
        navigate('/');
      } else {
        message.error(data.msg || t('twoFAVerifyFailed'));
      }
    } catch (error) {
      message.error(error.response?.data?.msg || t('twoFAVerifyFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '20px'
    }}>
      <Card style={{
        width: 450, boxShadow: '0 20px 40px rgba(0,0,0,0.1)', borderRadius: 16,
        border: 'none', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', position: 'relative', zIndex: 1
      }} bodyStyle={{ padding: '48px 40px' }}>
        <Select
          value={language}
          onChange={changeLanguage}
          style={{ position: 'absolute', top: 16, right: 16, width: 120 }}
          size="small"
          suffixIcon={<GlobalOutlined />}
          options={[
            { label: '🇨🇳 中文', value: 'zh-CN' },
            { label: '🇺🇸 English', value: 'en-US' },
          ]}
        />
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px', boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)'
          }}>
            <SafetyOutlined style={{ fontSize: 36, color: 'white' }} />
          </div>
          <h2 style={{ margin: 0, color: '#1f2937', fontSize: 24 }}>{t('loginTitle')}</h2>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 8 }}>{t('loginSubtitle')}</p>
        </div>

        {isLocked && (
          <Alert message={t('accountLocked')} description={t('accountLockedDesc', lockTime)}
            type="error" showIcon style={{ marginBottom: 24 }} />
        )}
        {!isLocked && loginAttempts > 0 && !show2FA && (
          <Alert message={t('loginFailedTimes', loginAttempts)}
            description={t('remainingAttempts', 5 - loginAttempts)}
            type="warning" showIcon style={{ marginBottom: 24 }} />
        )}
        {show2FA && (
          <Alert message={t('twoFAVerification')} description={t('twoFADesc')}
            type="info" showIcon style={{ marginBottom: 24 }} />
        )}

        {!show2FA ? (
          <Form onFinish={handleLogin} size="large" layout="vertical">
            <Form.Item name="username" rules={[{ required: true, message: t('usernameRequired') }]} style={{ marginBottom: 24 }}>
              <Input prefix={<UserOutlined style={{ color: '#9ca3af' }} />} placeholder={t('username')}
                disabled={isLocked} style={{ borderRadius: 8, border: '2px solid #e5e7eb', padding: '12px 16px', fontSize: 16 }} />
            </Form.Item>
            <Form.Item name="password" rules={[{ required: true, message: t('passwordRequired') }]}
              style={{ marginBottom: showCaptcha ? 24 : 32 }}>
              <Input.Password prefix={<LockOutlined style={{ color: '#9ca3af' }} />} placeholder={t('password')}
                disabled={isLocked} style={{ borderRadius: 8, border: '2px solid #e5e7eb', padding: '12px 16px', fontSize: 16 }} />
            </Form.Item>
            {showCaptcha && (
              <Form.Item name="captcha" rules={[{ required: true, message: t('captchaRequired') }]} style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div onClick={loadCaptcha} style={{
                      flex: 1, height: 48, borderRadius: 6, cursor: 'pointer',
                      border: '2px solid #e5e7eb', overflow: 'hidden',
                      background: '#ffffff', flexShrink: 0
                    }}>
                      {captchaImageUrl
                        ? <img src={captchaImageUrl} alt="captcha" style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 12 }}>{t('loading')}</div>
                      }
                    </div>
                    <Button onClick={loadCaptcha} style={{ height: 48, flexShrink: 0 }}>{t('refresh')}</Button>
                  </div>
                  <Input
                    placeholder={t('enterCaptcha')}
                    disabled={isLocked}
                    style={{ borderRadius: 8, border: '2px solid #e5e7eb', padding: '12px 16px', fontSize: 16 }}
                  />
                </div>
              </Form.Item>
            )}
            <Form.Item style={{ marginBottom: 24 }}>
              <Button type="primary" htmlType="submit" loading={loading} disabled={isLocked} block style={{
                height: 56, borderRadius: 8, fontSize: 18, fontWeight: 600,
                background: isLocked ? '#d1d5db' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none', boxShadow: isLocked ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}>
                {isLocked ? t('lockedButton', lockTime) : loading ? t('loggingIn') : t('login')}
              </Button>
            </Form.Item>
            <div style={{ textAlign: 'center', padding: '16px 20px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                <SafetyOutlined style={{ marginRight: 4 }} />{t('securityTip')}
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'pre-line' }}>{t('securityTipDesc')}</div>
            </div>
          </Form>
        ) : (
          <div>
            <Form.Item style={{ marginBottom: 24 }}>
              <Input placeholder={t('enter6DigitCode')} value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value)} maxLength={6} size="large"
                style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8, borderRadius: 8, border: '2px solid #e5e7eb', padding: '12px 16px' }} />
            </Form.Item>
            <Button type="primary" onClick={handleVerify2FA} loading={loading} block style={{
              height: 56, borderRadius: 8, fontSize: 18, fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)', marginBottom: 16
            }}>
              {loading ? t('verifying') : t('verify')}
            </Button>
            <Button onClick={() => { setShow2FA(false); setTempToken(null); setTwoFACode(''); if (showCaptcha) loadCaptcha(); }} block style={{ height: 48, borderRadius: 8 }}>
              {t('backToLogin')}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
